import { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { ArrowLeft, Check, HelpCircle, RotateCcw, Loader2, Flame, Star, Lock } from 'lucide-react';
import HanziWriterQuiz, { type HanziWriterQuizRef } from './HanziWriterQuiz';
import ChineseWritingGrid, { type ChineseWritingGridRef } from './ChineseWritingGrid';
import PrizeWheel from './PrizeWheel';
import HanziSessionSummary from './HanziSessionSummary';
import { useHanziSession } from './useHanziSession';
import {
  getHanziConfig,
  exampleZh,
  exampleEn,
  fetchDailyLogs,
  getRemedialPlanForStudent,
  type HanziExampleSentence,
  type HanziDailyLog,
  type HanziRemedialStepType,
} from '../../services/hanziHeroService';
import { fetchStudentsForTutee } from '../../services/tuteeService';
import { getPinyinDisplay, tokenizeForRuby } from '../../utils/pinyinUtils';
import { formatSingaporeSpinTimestamp } from '../../utils/singaporeTimeFormat';
import { Tutee } from '../../types/tuition';

const SELF_CHECK_ANSWER_GRID_SIZE = 252;

/**
 * Missing-character hint pinyin: vertical gap above the underline.
 * Tune in one place: larger negative = closer to the line (try -10px … -14px). Too far overlaps the rule.
 */
const MISSING_HINT_PINYIN_MARGIN_BOTTOM = '-1rem';

/** Remedial panels match main practice grid size. */
const REMEDIAL_MAIN_PANEL_SIZE = 400;

function nextRemedialPreviewMessage(next: HanziRemedialStepType | undefined): string {
  if (!next) return 'Almost there.';
  if (next === 'writer_guided') return 'Next — trace with stroke guide.';
  if (next === 'grid_with_model') return 'Next — copy on the grid with 范字.';
  return 'Next — blank grid, write from memory.';
}

function remedialStepShortLabel(t: HanziRemedialStepType): string {
  if (t === 'writer_guided') return 'Trace';
  if (t === 'grid_with_model') return '范字';
  return 'Blank';
}

/** Plain Chinese or per-character pinyin ruby when `showRuby`. */
function SentenceFragmentWithOptionalRuby({
  text,
  showRuby,
}: {
  text: string;
  showRuby: boolean;
}) {
  if (!showRuby) {
    return (
      <span className="font-serif" style={{ fontFamily: 'KaTi Maobi, KaiTi, serif' }}>
        {text}
      </span>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-end justify-center gap-x-px gap-y-0.5">
      {tokenizeForRuby(text).map((tok, i) =>
        tok.kind === 'hanzi' ? (
          <span key={i} className="inline-flex flex-col items-center justify-end">
            <span className="text-[10px] sm:text-xs text-amber-600 leading-tight mb-0.5 max-w-[3.2em] text-center break-words">
              {tok.pinyin}
            </span>
            <span
              className="font-serif text-xl sm:text-2xl leading-none"
              style={{ fontFamily: 'KaTi Maobi, KaiTi, serif' }}
            >
              {tok.char}
            </span>
          </span>
        ) : (
          <span
            key={i}
            className="font-serif text-xl sm:text-2xl self-end leading-none pb-px"
            style={{ fontFamily: 'KaTi Maobi, KaiTi, serif' }}
          >
            {tok.char === ' ' ? '\u00a0' : tok.char}
          </span>
        )
      )}
    </span>
  );
}

interface HanziHeroProps {
  tutee: Tutee;
  onBack: () => void;
  config?: Record<string, unknown>;
}

const HanziHero = ({ tutee, onBack, config: configOverride }: HanziHeroProps) => {
  const config = getHanziConfig(configOverride);
  const prizeWheelMinStreak = Math.max(1, config.prizeWheelMinStreak ?? 1);
  const writerRemedialRef = useRef<HanziWriterQuizRef | null>(null);
  const writerRemedialStepRef = useRef(0);
  const remedialPlanRef = useRef<HanziRemedialStepType[]>(getRemedialPlanForStudent(config, undefined));
  const remedialAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gridRef = useRef<ChineseWritingGridRef | null>(null);
  const remedialGridRef = useRef<ChineseWritingGridRef | null>(null);
  /** Main practice UI: block text selection / drag-select so palm + stylus doesn’t “select all”. */
  const practiceSurfaceRef = useRef<HTMLDivElement>(null);
  const [useCanvasFallback, setUseCanvasFallback] = useState(false);
  const [showSelfCheck, setShowSelfCheck] = useState(false);
  /** Recall phase: free write first. Wrong → show HanziWriter. */
  const [showHanziWriter, setShowHanziWriter] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [showEnglish, setShowEnglish] = useState(false);
  const [visibleSentenceCount, setVisibleSentenceCount] = useState(1);
  /** PNG of free-write grid when user taps "No, show me" */
  const [userAttemptSnapshot, setUserAttemptSnapshot] = useState<string | null>(null);
  /** 0..2 = which HanziWriter box is active; 3 means finished (briefly before unmount) */
  const [writerRemedialStep, setWriterRemedialStep] = useState(0);
  /** Writer step index while showing success overlay before advancing */
  const [remedialSuccessSlot, setRemedialSuccessSlot] = useState<number | null>(null);
  useEffect(() => {
    writerRemedialStepRef.current = writerRemedialStep;
  }, [writerRemedialStep]);

  const [winningLabel, setWinningLabel] = useState<string | null>(null);
  const [spinResultAt, setSpinResultAt] = useState<Date | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [students, setStudents] = useState<{ value: string; label: string }[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<string | undefined>(undefined);
  const [hasChosenStudent, setHasChosenStudent] = useState(false);
  const [summaryLogs, setSummaryLogs] = useState<HanziDailyLog[]>([]);
  const [summaryLogsLoading, setSummaryLogsLoading] = useState(false);
  /** Main recall grid: any strokes → show Check; empty → show I am not sure. */
  const [mainPracticeGridHasInk, setMainPracticeGridHasInk] = useState(false);
  /** Remedial grid step: require strokes before Continue / Submit. */
  const [remedialGridHasInk, setRemedialGridHasInk] = useState(false);

  useEffect(() => {
    const load = async () => {
      const list = await fetchStudentsForTutee(tutee.id);
      const opts = list.map((s) => ({
        value: s.studentName.toLowerCase(),
        label: s.studentName.charAt(0).toUpperCase() + s.studentName.slice(1),
      }));
      setStudents(opts);
      if (opts.length === 1) {
        setSelectedStudent(opts[0].value);
        setHasChosenStudent(true);
      } else if (opts.length === 0) {
        setHasChosenStudent(true);
      }
    };
    load();
  }, [tutee.id]);

  const {
    currentWord,
    sessionWords,
    currentIndex,
    phase,
    streak,
    characterProgress,
    handleQuizComplete,
    sessionOutcomeByCharacter,
    markOk,
    markPracticeAgain,
    completeSession,
    isLoading,
    error,
    isEnsuringExamples,
  } = useHanziSession({
    tuteeId: tutee.id,
    studentName: selectedStudent,
    config,
  });

  const remedialPlan = useMemo(
    () => getRemedialPlanForStudent(config, selectedStudent),
    [config, selectedStudent]
  );
  remedialPlanRef.current = remedialPlan;

  useEffect(() => {
    if (phase !== 'session_complete' || sessionWords.length === 0) {
      setSummaryLogs([]);
      setSummaryLogsLoading(false);
      return;
    }
    let cancelled = false;
    setSummaryLogsLoading(true);
    const studentKey = selectedStudent ?? '';
    fetchDailyLogs(tutee.id, studentKey, 90)
      .then((logs) => {
        if (!cancelled) setSummaryLogs(logs);
      })
      .catch(() => {
        if (!cancelled) setSummaryLogs([]);
      })
      .finally(() => {
        if (!cancelled) setSummaryLogsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [phase, sessionWords.length, tutee.id, selectedStudent]);

  useEffect(() => {
    setUseCanvasFallback(false);
    setShowHanziWriter(false);
    setShowHint(false);
    setShowEnglish(false);
    setVisibleSentenceCount(1);
    setUserAttemptSnapshot(null);
    setWriterRemedialStep(0);
    setRemedialSuccessSlot(null);
    if (remedialAdvanceTimerRef.current) {
      clearTimeout(remedialAdvanceTimerRef.current);
      remedialAdvanceTimerRef.current = null;
    }
    remedialGridRef.current?.clearCanvas();
    setMainPracticeGridHasInk(false);
    setRemedialGridHasInk(false);
  }, [currentWord?.character]);

  useEffect(() => {
    setRemedialGridHasInk(false);
  }, [writerRemedialStep]);

  useEffect(() => {
    return () => {
      if (remedialAdvanceTimerRef.current) {
        clearTimeout(remedialAdvanceTimerRef.current);
        remedialAdvanceTimerRef.current = null;
      }
    };
  }, []);

  useLayoutEffect(() => {
    const root = practiceSurfaceRef.current;
    if (!root) return;
    const stopSelectIfInside = (e: Event) => {
      const t = e.target;
      if (t instanceof Node && root.contains(t)) e.preventDefault();
    };
    document.addEventListener('selectstart', stopSelectIfInside, true);
    document.addEventListener('dragstart', stopSelectIfInside, true);
    return () => {
      document.removeEventListener('selectstart', stopSelectIfInside, true);
      document.removeEventListener('dragstart', stopSelectIfInside, true);
    };
  }, [currentWord?.character, phase, showHanziWriter]);

  /** Pinch / ctrl+wheel zoom makes handwriting misaligned; block it on the active practice screen only. */
  useLayoutEffect(() => {
    const root = practiceSurfaceRef.current;
    if (!root) return;

    const preventZoomWheel = (e: WheelEvent) => {
      if (e.ctrlKey) e.preventDefault();
    };
    const preventSafariGestureZoom = (e: Event) => {
      e.preventDefault();
    };
    const preventMultiTouchZoom = (e: TouchEvent) => {
      if (e.touches.length > 1) e.preventDefault();
    };

    const opts: AddEventListenerOptions = { passive: false };
    root.addEventListener('wheel', preventZoomWheel, opts);
    root.addEventListener('gesturestart', preventSafariGestureZoom, opts);
    root.addEventListener('gesturechange', preventSafariGestureZoom, opts);
    root.addEventListener('gestureend', preventSafariGestureZoom, opts);
    root.addEventListener('touchmove', preventMultiTouchZoom, opts);

    return () => {
      root.removeEventListener('wheel', preventZoomWheel);
      root.removeEventListener('gesturestart', preventSafariGestureZoom);
      root.removeEventListener('gesturechange', preventSafariGestureZoom);
      root.removeEventListener('gestureend', preventSafariGestureZoom);
      root.removeEventListener('touchmove', preventMultiTouchZoom);
    };
  }, [currentWord?.character, phase, showHanziWriter]);

  const sentences = useMemo((): HanziExampleSentence[] => {
    if (!currentWord) return [];
    const fromArr = currentWord.exampleSentences?.filter((s) => exampleZh(s));
    if (fromArr?.length) return fromArr;
    return currentWord.exampleSentence ? [{ zh: currentWord.exampleSentence }] : [];
  }, [currentWord?.character, currentWord?.exampleSentence, currentWord?.exampleSentences]);

  const hasAnyEnglish = useMemo(() => sentences.some((s) => !!exampleEn(s)), [sentences]);

  const shuffledSentences = useMemo(() => {
    if (sentences.length <= 1) return sentences;
    const arr = [...sentences];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [currentWord?.character, sentences]);

  const visibleSentences = useMemo(
    () => shuffledSentences.slice(0, visibleSentenceCount),
    [shuffledSentences, visibleSentenceCount]
  );

  const handleQuizCompleteCallback = (summary: { character: string; totalMistakes: number }) => {
    const correct = summary.totalMistakes === 0;
    handleQuizComplete(summary.character, correct);
    setShowSelfCheck(true);
  };

  const handleWriterLoadError = () => {
    setUseCanvasFallback(true);
    setShowHanziWriter(false);
  };

  const handleOk = () => {
    setShowSelfCheck(false);
    markOk();
  };

  const handlePracticeAgain = () => {
    setShowSelfCheck(false);
    const step = writerRemedialStepRef.current;
    const plan = remedialPlanRef.current;
    const type = plan[step];
    if (type === 'writer_guided') writerRemedialRef.current?.restartQuiz();
    else remedialGridRef.current?.clearCanvas();
    markPracticeAgain();
  };

  const handleCheck = () => setShowSelfCheck(true);

  const handleGotItRight = () => {
    handleQuizComplete(currentWord!.character, true);
    setShowSelfCheck(false);
    gridRef.current?.clearCanvas();
    markOk();
  };

  const handleGotItWrong = () => {
    if (remedialAdvanceTimerRef.current) {
      clearTimeout(remedialAdvanceTimerRef.current);
      remedialAdvanceTimerRef.current = null;
    }
    setRemedialSuccessSlot(null);
    handleQuizComplete(currentWord!.character, false);
    setShowSelfCheck(false);
    try {
      if (gridRef.current?.hasUserInk()) {
        setUserAttemptSnapshot(
          gridRef.current?.toCompositeDataURL() ?? gridRef.current?.toDataURL() ?? null
        );
      } else {
        setUserAttemptSnapshot(null);
      }
    } catch {
      setUserAttemptSnapshot(null);
    }
    setWriterRemedialStep(0);
    setShowHanziWriter(true);
  };

  /** Finishing the last remedial step; recall was already recorded at self-check. */
  const finishRemedialSession = useCallback(() => {
    if (!currentWord) return;
    setShowHanziWriter(false);
    setUserAttemptSnapshot(null);
    setWriterRemedialStep(0);
    setRemedialSuccessSlot(null);
    remedialGridRef.current?.clearCanvas();
    markOk();
  }, [currentWord, markOk]);

  const handleRemedialGridSubmit = useCallback(() => {
    if (!remedialGridRef.current?.hasUserInk()) return;
    const plan = remedialPlanRef.current;
    const step = writerRemedialStepRef.current;
    if (step >= plan.length - 1) {
      finishRemedialSession();
      return;
    }
    remedialGridRef.current?.clearCanvas();
    setWriterRemedialStep((s) => s + 1);
  }, [finishRemedialSession]);

  const handleWriterSlotComplete = useCallback((_summary: { character: string; totalMistakes: number }) => {
    const plan = remedialPlanRef.current;
    const step = writerRemedialStepRef.current;
    if (step >= plan.length - 1) {
      finishRemedialSession();
      return;
    }
    if (remedialAdvanceTimerRef.current) {
      clearTimeout(remedialAdvanceTimerRef.current);
      remedialAdvanceTimerRef.current = null;
    }
    setRemedialSuccessSlot(step);
    remedialAdvanceTimerRef.current = setTimeout(() => {
      remedialAdvanceTimerRef.current = null;
      setRemedialSuccessSlot(null);
      setWriterRemedialStep((s) => s + 1);
    }, 820);
  }, [finishRemedialSession]);

  const handleRecordAndSpin = async () => {
    setIsRecording(true);
    await completeSession();
    setIsRecording(false);
  };

  const handleWheelComplete = (label: string) => {
    setWinningLabel(label);
    setSpinResultAt(new Date());
  };

  const gradientClass = tutee.colorScheme.gradient;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-amber-600" />
      </div>
    );
  }

  if (students.length > 1 && !hasChosenStudent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 p-6 flex flex-col">
        <button
          onClick={onBack}
          className="self-start flex items-center gap-2 text-amber-700 hover:text-amber-800 font-bold mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>
        <div className="flex-1 flex flex-col items-center justify-center gap-6">
          <h2 className="text-2xl font-bold text-gray-800">Who is practicing?</h2>
          <div className="flex flex-col gap-3 w-full max-w-xs">
            {students.map((s) => (
              <button
                key={s.value}
                onClick={() => {
                  setSelectedStudent(s.value);
                  setHasChosenStudent(true);
                }}
                className={`p-4 rounded-2xl font-bold text-lg border-2 transition-all ${
                  selectedStudent === s.value
                    ? 'border-amber-500 bg-amber-50 text-amber-800'
                    : 'border-gray-200 bg-white hover:border-amber-200'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 p-6 flex flex-col items-center justify-center">
        <p className="text-red-600 font-medium mb-4">{error}</p>
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl font-bold"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
      </div>
    );
  }

  if (phase === 'session_complete' && sessionWords.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 p-6 flex flex-col items-center justify-center gap-6">
        <p className="text-lg text-gray-700 text-center">
          No characters to practice. Ask your tutor to add words in the Hanzi Hero settings.
        </p>
        <button
          onClick={onBack}
          className={`flex items-center gap-2 px-6 py-3 bg-gradient-to-r ${gradientClass} text-white rounded-xl font-bold shadow-lg`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (phase === 'session_complete' && sessionWords.length > 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 pb-10 pt-2 px-4 sm:px-6">
        <div className="mx-auto max-w-xl">
          <button
            type="button"
            onClick={onBack}
            className="mb-4 flex items-center gap-2 font-bold text-amber-800 hover:text-amber-900"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden />
            Back
          </button>
          <HanziSessionSummary
            sessionWords={sessionWords}
            sessionOutcomeByCharacter={sessionOutcomeByCharacter}
            dailyLogs={summaryLogs}
            logsLoading={summaryLogsLoading}
            gradientClass={gradientClass}
            prizeWheelMinStreak={prizeWheelMinStreak}
            isRecording={isRecording}
            onRecord={handleRecordAndSpin}
          />
        </div>
      </div>
    );
  }

  if (phase === 'recorded_no_wheel') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 p-6 flex flex-col">
        <button
          onClick={onBack}
          className="self-start flex items-center gap-2 text-amber-700 hover:text-amber-800 font-bold mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>

        <div className="flex-1 flex flex-col items-center justify-center gap-6 text-center max-w-md mx-auto">
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-xl">
            <Flame className="w-5 h-5 text-amber-600" />
            <span className="font-bold text-amber-800">
              {streak} day streak — check-in saved
            </span>
          </div>
          <div>
            <h2 className="text-xl font-black text-gray-800 mb-2">Prize wheel locked</h2>
            <p className="text-gray-600 leading-relaxed">
              Your tutor set the wheel to unlock at a <strong>{prizeWheelMinStreak}</strong>-day streak after
              check-in. Keep practicing on consecutive days to earn a spin.
            </p>
          </div>
          <button
            onClick={onBack}
            className={`px-6 py-3 bg-gradient-to-r ${gradientClass} text-white rounded-xl font-bold`}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'wheel') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 p-6 flex flex-col">
        <button
          onClick={onBack}
          className="self-start flex items-center gap-2 text-amber-700 hover:text-amber-800 font-bold mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>

        <div className="flex-1 flex flex-col items-center justify-center gap-6">
          {streak > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-xl">
              <Flame className="w-5 h-5 text-amber-600" />
              <span className="font-bold text-amber-800">{streak} day streak!</span>
            </div>
          )}

          <PrizeWheel
            segments={config.prizeWheelSegments ?? []}
            onSpinComplete={handleWheelComplete}
            singleUse
          />

          {winningLabel && (
            <div className="w-full max-w-md mx-auto px-2 text-center animate-fade-in space-y-5">
              <div className="rounded-2xl border border-amber-200/90 bg-white/95 shadow-md px-5 py-6 sm:px-8 sm:py-7">
                <p className="text-xl sm:text-2xl font-bold text-gray-900 leading-snug text-pretty">
                  {winningLabel}
                </p>
                {spinResultAt && (
                  <p className="mt-5 text-sm text-gray-500 font-medium tabular-nums tracking-tight">
                    {formatSingaporeSpinTimestamp(spinResultAt)}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={onBack}
                className={`px-6 py-3 bg-gradient-to-r ${gradientClass} text-white rounded-xl font-bold shadow-md`}
              >
                Back to Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!currentWord) return null;

  return (
    <div
      ref={practiceSurfaceRef}
      className="min-h-screen touch-manipulation overscroll-y-contain bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 pb-safe select-none [-webkit-touch-callout:none]"
    >
      <div className="max-w-2xl mx-auto px-4 py-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-amber-700 hover:text-amber-800 font-bold mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>

        <div className="mb-4 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-500">
            Character {currentIndex + 1} of {sessionWords.length}
          </span>
          {streak > 0 && (
            <span className="flex items-center gap-1 text-amber-600 font-bold">
              <Flame className="w-4 h-4" />
              {streak}
            </span>
          )}
        </div>

        {(currentWord.moeLevel || currentWord.moeLessonNumber || currentWord.moeType) && (
          <div className="mb-2 flex flex-wrap items-center justify-center gap-2 text-xs font-medium text-amber-700">
            {currentWord.moeLevel && (
              <span className="px-2 py-0.5 bg-amber-100 rounded-md">
                {currentWord.moeLevel}
              </span>
            )}
            {currentWord.moeLessonNumber != null && (
              <span className="px-2 py-0.5 bg-amber-100 rounded-md">
                第{currentWord.moeLessonNumber}课
              </span>
            )}
            {currentWord.moeType && (
              <span className="px-2 py-0.5 bg-amber-100 rounded-md">
                {currentWord.moeType === 'write' ? '识写' : '识读'}
              </span>
            )}
          </div>
        )}

        {showHanziWriter ? (
          <>
            <div className="mb-4 text-center">
              <p className="text-sm text-amber-800/90 font-semibold mb-2">Practice writing</p>
              <p
                className="text-3xl font-serif text-gray-800 mb-1"
                style={{ fontFamily: 'KaTi Maobi, KaiTi, serif' }}
              >
                {currentWord.character}
              </p>
              <p className="text-base text-gray-500">
                {getPinyinDisplay(currentWord.character, currentWord.pinyin)}
              </p>
            </div>
            {!userAttemptSnapshot && shuffledSentences.some((s) => exampleZh(s)) && (
              <div className="mb-5 max-w-2xl mx-auto w-full">
                <div className="p-4 bg-white rounded-xl border-2 border-amber-200 space-y-4">
                  {shuffledSentences.map((sent, idx) => {
                    const zh = exampleZh(sent);
                    if (!zh) return null;
                    const en = exampleEn(sent);
                    return (
                      <div
                        key={idx}
                        className="space-y-2 border-b border-amber-100 pb-4 last:border-b-0 last:pb-0"
                      >
                        <div className="flex flex-wrap items-end justify-center gap-x-1 gap-y-1">
                          <SentenceFragmentWithOptionalRuby text={zh} showRuby />
                        </div>
                        {en ? (
                          <p className="text-center text-sm text-gray-600 leading-relaxed px-1">{en}</p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {useCanvasFallback ? (
              <>
                {userAttemptSnapshot && (
                  <div className="mb-6 p-3 bg-amber-50/60 rounded-xl border border-amber-200">
                    <p className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-2 text-center">
                      Your attempt
                    </p>
                    <img
                      src={userAttemptSnapshot}
                      alt="What you wrote on the practice grid"
                      className="max-h-56 mx-auto rounded-lg border border-amber-200 bg-white shadow-sm object-contain"
                    />
                  </div>
                )}
                <ChineseWritingGrid
                  ref={gridRef}
                  gridStyle={config.gridStyle ?? 'tianzige'}
                  modelCharacter={
                    config.showModelCharacter !== false ? currentWord.character : undefined
                  }
                  size={400}
                  onInkChange={setMainPracticeGridHasInk}
                />
                <button
                  onClick={() => {
                    setShowHanziWriter(false);
                    setUserAttemptSnapshot(null);
                    setWriterRemedialStep(0);
                    markOk();
                  }}
                  className={`mt-6 w-full flex items-center justify-center gap-2 py-4 bg-gradient-to-r ${gradientClass} text-white rounded-xl font-bold text-lg shadow-lg`}
                >
                  <Check className="w-5 h-5" />
                  Done practicing
                </button>
              </>
            ) : (
              <>
                {userAttemptSnapshot && (
                  <div className="mb-6 p-3 bg-amber-50/60 rounded-xl border border-amber-200">
                    <p className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-2 text-center">
                      Your attempt
                    </p>
                    <img
                      src={userAttemptSnapshot}
                      alt="What you wrote on the practice grid"
                      className="max-h-56 mx-auto rounded-lg border border-amber-200 bg-white shadow-sm object-contain"
                    />
                  </div>
                )}
                <div className="flex flex-wrap items-stretch justify-center gap-1.5 sm:gap-2 max-w-[min(100vw-2rem,28rem)] mx-auto mb-4">
                  {remedialPlan.map((stepType, slot) => {
                    const done = slot < writerRemedialStep;
                    const active = slot === writerRemedialStep;
                    return (
                      <div key={slot} className="flex flex-1 flex-col items-center gap-1 min-w-[2.75rem] max-w-[4.5rem]">
                        <div
                          className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl border-2 flex items-center justify-center transition-all ${
                            done
                              ? 'border-green-400 bg-green-50 shadow-sm'
                              : active
                                ? 'border-amber-500 bg-white shadow-md ring-2 ring-amber-300/40'
                                : 'border-dashed border-gray-300 bg-gray-50/90'
                          }`}
                          title={remedialStepShortLabel(stepType)}
                        >
                          {done ? (
                            <Check className="w-5 h-5 sm:w-5 sm:h-5 text-green-500" strokeWidth={2.5} />
                          ) : active ? (
                            <span className="text-[10px] sm:text-xs font-black text-amber-700 text-center leading-tight px-0.5">
                              {remedialStepShortLabel(stepType)}
                            </span>
                          ) : (
                            <Lock className="w-3.5 h-3.5 text-gray-400" strokeWidth={2} />
                          )}
                        </div>
                        <span
                          className={`text-[8px] sm:text-[9px] font-bold uppercase tracking-wide text-center leading-tight px-0.5 ${
                            done ? 'text-green-600' : active ? 'text-amber-800' : 'text-gray-400'
                          }`}
                        >
                          {slot + 1}
                          {active ? '·now' : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-col items-center w-full pb-2">
                  {remedialPlan[writerRemedialStep] === 'writer_guided' ? (
                    <div className="relative w-full min-w-0 max-w-[min(100vw-2rem,28rem)] mx-auto rounded-2xl border-2 border-amber-400/90 bg-white p-1 shadow-md overflow-hidden flex flex-col items-center">
                      {remedialSuccessSlot !== null && (
                        <div
                          className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 rounded-xl bg-gradient-to-b from-emerald-50/95 via-white/90 to-amber-50/80 backdrop-blur-[2px] pointer-events-none animate-fade-in"
                          aria-live="polite"
                        >
                          <div className="animate-scale-in-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 p-5 shadow-lg shadow-emerald-500/30">
                            <Check className="w-12 h-12 text-white" strokeWidth={3} />
                          </div>
                          <p className="text-xl font-black text-emerald-900 px-4 text-center">Nice tracing!</p>
                          <p className="text-sm font-semibold text-emerald-800/90 px-6 text-center max-w-[18rem]">
                            {nextRemedialPreviewMessage(remedialPlan[writerRemedialStep + 1])}
                          </p>
                        </div>
                      )}
                      <HanziWriterQuiz
                        key={`remedial-writer-${writerRemedialStep}-${currentWord.character}`}
                        ref={(el) => {
                          writerRemedialRef.current = el;
                        }}
                        character={currentWord.character}
                        size={REMEDIAL_MAIN_PANEL_SIZE}
                        variant="strict"
                        delayBetweenStrokesMs={config.writerDelayBetweenStrokesMs}
                        showOutline={config.writerQuizShowOutline !== false}
                        onComplete={handleWriterSlotComplete}
                        onLoadError={handleWriterLoadError}
                      />
                    </div>
                  ) : (
                    <div className="w-full max-w-[min(100vw-2rem,28rem)] mx-auto flex flex-col items-stretch gap-4">
                      <ChineseWritingGrid
                        ref={remedialGridRef}
                        gridStyle={config.gridStyle ?? 'tianzige'}
                        modelCharacter={
                          remedialPlan[writerRemedialStep] === 'grid_with_model'
                            ? currentWord.character
                            : undefined
                        }
                        size={REMEDIAL_MAIN_PANEL_SIZE}
                        onInkChange={setRemedialGridHasInk}
                      />
                      {remedialGridHasInk ? (
                        <button
                          type="button"
                          onClick={handleRemedialGridSubmit}
                          className={`w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-base font-bold bg-gradient-to-r ${gradientClass} text-white shadow-lg hover:opacity-95 active:scale-[0.99] touch-manipulation`}
                        >
                          <Check className="w-5 h-5" aria-hidden />
                          {writerRemedialStep >= remedialPlan.length - 1 ? 'Submit' : 'Continue'}
                        </button>
                      ) : (
                        <p className="text-center text-sm font-medium text-gray-600 py-3 px-2">
                          Draw on the grid to continue.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <div className="mb-6">
              <p className="text-sm text-gray-500 mb-3 text-center">
                Fill in the blank from memory. Use the buttons for pinyin or English — tap the sentence or a
                translation again to hide.
              </p>
              <div className="p-4 bg-white rounded-xl border-2 border-amber-200 space-y-4">
                {isEnsuringExamples && visibleSentences.length === 0 && (
                  <div className="flex flex-col items-center justify-center gap-2 py-6 text-gray-500">
                    <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
                    <span className="text-sm">Preparing example sentences…</span>
                  </div>
                )}
                {visibleSentences.map((sent, idx) => {
                  const char = currentWord.character;
                  const zh = exampleZh(sent);
                  const en = exampleEn(sent);
                  const fillIn =
                    zh && zh.includes(char) ? zh.replace(char, '_') : `Write the character for: _`;
                  const u = fillIn.indexOf('_');
                  const left = u >= 0 ? fillIn.slice(0, u) : fillIn;
                  const right = u >= 0 ? fillIn.slice(u + 1) : '';
                  return (
                    <div key={idx} className="space-y-1">
                      <div
                        className={
                          showHint
                            ? 'cursor-pointer rounded-lg px-1 -mx-1 py-0.5 hover:bg-amber-50/80 active:bg-amber-100/60 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-amber-400'
                            : undefined
                        }
                        role={showHint ? 'button' : undefined}
                        tabIndex={showHint ? 0 : undefined}
                        aria-label={showHint ? 'Tap to hide pinyin' : undefined}
                        onClick={showHint ? () => setShowHint(false) : undefined}
                        onKeyDown={
                          showHint
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setShowHint(false);
                                }
                              }
                            : undefined
                        }
                      >
                        <div className="flex flex-wrap items-end justify-center gap-x-1 gap-y-1">
                          <SentenceFragmentWithOptionalRuby text={left} showRuby={showHint} />
                          <span className="inline-flex flex-col items-center justify-end gap-0 self-end shrink-0 px-2.5 sm:px-3 min-w-[2.25rem]">
                            {showHint && (
                              <span
                                className="text-base sm:text-lg font-bold text-amber-700 leading-none block"
                                style={{ marginBottom: MISSING_HINT_PINYIN_MARGIN_BOTTOM }}
                              >
                                {getPinyinDisplay(char, currentWord.pinyin)}
                              </span>
                            )}
                            <span
                              className="inline-block w-[1.1em] min-h-[1.05em] shrink-0 border-b border-gray-400"
                              aria-label="Missing character"
                            />
                          </span>
                          <SentenceFragmentWithOptionalRuby text={right} showRuby={showHint} />
                        </div>
                      </div>
                      {showEnglish && en && (
                        <p
                          role="button"
                          tabIndex={0}
                          className="text-center text-sm text-gray-600 px-2 py-1 rounded-md cursor-pointer hover:text-gray-800 hover:bg-slate-100/80 active:bg-slate-200/60 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                          onClick={() => setShowEnglish(false)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setShowEnglish(false);
                            }
                          }}
                        >
                          {en}
                        </p>
                      )}
                    </div>
                  );
                })}
                {!showHint && (
                  <button
                    type="button"
                    onClick={() => setShowHint(true)}
                    className="w-full py-2 text-sm font-medium text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
                  >
                    Hint (show pinyin)
                  </button>
                )}
                {hasAnyEnglish && !showEnglish && (
                  <button
                    type="button"
                    onClick={() => setShowEnglish(true)}
                    className="w-full py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded-lg transition-colors border border-slate-200"
                  >
                    Show English
                  </button>
                )}
                {visibleSentenceCount < shuffledSentences.length && (
                  <button
                    type="button"
                    onClick={() => setVisibleSentenceCount((n) => n + 1)}
                    className="w-full py-2 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors border border-gray-200"
                  >
                    More examples
                  </button>
                )}
              </div>
              {(() => {
                const stats = characterProgress[currentWord.character];
                const attempts = stats?.attemptCount ?? 0;
                if (attempts === 0) {
                  return (
                    <p className="mt-3 text-sm text-gray-500 text-center">
                      Seeing this word for the first time!
                    </p>
                  );
                }
                const correct = stats?.correctCount ?? 0;
                return (
                  <p className="mt-3 text-sm text-gray-500 text-center">
                    You've practiced this word {attempts} time{attempts !== 1 ? 's' : ''}. {correct}{' '}
                    correct attempt{correct !== 1 ? 's' : ''}!
                  </p>
                );
              })()}
            </div>

            <ChineseWritingGrid
              ref={gridRef}
              gridStyle={config.gridStyle ?? 'tianzige'}
              modelCharacter={
                config.showModelCharacter !== false ? currentWord.character : undefined
              }
              size={400}
              onInkChange={setMainPracticeGridHasInk}
            />

            {showSelfCheck ? (
              <div className="mt-6 p-5 sm:p-7 bg-white rounded-2xl border-2 border-amber-200 shadow-lg max-w-lg mx-auto w-full">
                <p className="text-center text-xs sm:text-sm font-bold text-amber-800/80 uppercase tracking-widest mb-5">
                  The answer is
                </p>
                <div className="flex flex-col items-center gap-4 mb-6">
                  <ChineseWritingGrid
                    readOnly
                    gridStyle={config.gridStyle ?? 'tianzige'}
                    modelCharacter={currentWord.character}
                    modelClassName="text-amber-950"
                    size={SELF_CHECK_ANSWER_GRID_SIZE}
                    className="w-full [&>div>div]:mx-auto"
                  />
                  <p
                    className="text-center text-lg sm:text-xl font-bold text-gray-900 w-full px-2"
                    style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                  >
                    {getPinyinDisplay(currentWord.character, currentWord.pinyin)}
                  </p>
                </div>
                <div className="border-t border-amber-100 pt-5 mt-1">
                  <p className="text-center text-base sm:text-lg font-semibold text-gray-800 mb-5 px-2">
                    Did you write it correctly?
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-3">
                    <button
                      type="button"
                      onClick={handleGotItWrong}
                      className="flex-1 flex items-center justify-center gap-2 py-3.5 px-4 bg-gray-100 text-gray-800 rounded-xl font-bold hover:bg-gray-200 border border-gray-200/80 transition-colors"
                    >
                      <RotateCcw className="w-5 h-5 shrink-0" />
                      No, show me
                    </button>
                    <button
                      type="button"
                      onClick={handleGotItRight}
                      className={`flex-1 flex items-center justify-center gap-2 py-3.5 px-4 bg-gradient-to-r ${gradientClass} text-white rounded-xl font-bold shadow-lg transition-opacity hover:opacity-95`}
                    >
                      <Check className="w-5 h-5 shrink-0" />
                      Yes, next
                    </button>
                  </div>
                </div>
              </div>
            ) : mainPracticeGridHasInk ? (
              <button
                type="button"
                onClick={handleCheck}
                className={`mt-6 w-full flex items-center justify-center gap-2 py-4 bg-gradient-to-r ${gradientClass} text-white rounded-xl font-bold text-lg shadow-lg`}
              >
                <Star className="w-5 h-5" aria-hidden />
                Check
              </button>
            ) : (
              <button
                type="button"
                onClick={handleGotItWrong}
                className="mt-6 w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold text-lg border-2 border-amber-500 bg-white text-amber-900 shadow-md hover:bg-amber-50 active:scale-[0.99] touch-manipulation"
              >
                <HelpCircle className="w-5 h-5 shrink-0" aria-hidden />
                I am not sure
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HanziHero;
